import React from 'react';
import { useTranslation } from 'react-i18next';
import { Conversation, Message } from '../../../services/chatService';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { getMediaUrl } from '../../../utils/media';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { User } from '../../../types';

interface ChatListProps {
    conversations: Conversation[];
    selectedId?: number;
    onSelect: (c: Conversation) => void;
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    currentTime: number; // for forcing re-render of times
}

export const ChatList: React.FC<ChatListProps> = ({
    conversations,
    selectedId,
    onSelect,
    searchQuery,
    setSearchQuery,
    currentTime
}) => {
    const { t, i18n } = useTranslation('common');

    const formatTime = (dateString?: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date(currentTime);
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return t('chat.status.justNow', { defaultValue: 'Just now' });
        if (minutes < 60) return t('chat.status.minutesAgo', { count: minutes, defaultValue: '{{count}}m ago' });
        if (hours < 24) return t('chat.status.hoursAgo', { count: hours, defaultValue: '{{count}}h ago' });
        if (days < 7) return t('chat.status.daysAgo', { count: days, defaultValue: '{{count}}d ago' });

        return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
    };

    const filtered = conversations.filter(c =>
        c.other_user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.other_user.full_name && c.other_user.full_name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div className="flex flex-col h-full bg-white border-r">
            <div className="p-4 border-b">
                <h2 className="text-xl font-bold mb-4">{t('chat.header.chats', { defaultValue: 'Chats' })}</h2>
                <Input
                    placeholder={t('chat.searchPlaceholder', { defaultValue: 'Search...' })}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-gray-50"
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                {filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        {t('chat.noConversations', { defaultValue: 'No conversations found' })}
                    </div>
                ) : (
                    <div className="space-y-1">
                        {filtered.map(conv => (
                            <div
                                key={conv.id}
                                onClick={() => onSelect(conv)}
                                className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${selectedId === conv.id ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                            >
                                <div className="relative">
                                    <Avatar className="w-12 h-12">
                                        <AvatarImage src={getMediaUrl(conv.other_user.avatar)} />
                                        <AvatarFallback>{(conv.other_user.full_name || conv.other_user.username)[0].toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    {conv.other_user.is_online && (
                                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-baseline mb-1">
                                        <h3 className="font-semibold truncate text-gray-900">
                                            {conv.other_user.full_name || conv.other_user.username}
                                        </h3>
                                        <span className="text-xs text-gray-500 flex-shrink-0">
                                            {formatTime(conv.last_message_at)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                                            {conv.last_message?.content || t('chat.imageSent', { defaultValue: 'Sent an image' })}
                                        </p>
                                        {conv.unread_count > 0 && (
                                            <Badge variant="default" className="ml-2 h-5 w-5 flex items-center justify-center p-0 rounded-full">
                                                {conv.unread_count}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
